import { Snackbar } from 'components/Snackbar';
import { useLoader } from 'hooks/utilHooks';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { RepresentativeService } from 'services';
import {
  IRepresentant,
  IRepresentantResult,
  RepresentantDefaultValues,
} from 'types/Representant';
import { IPaginatedRequest } from 'types/Util';

import { AlertColor } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { representativeValidation } from 'schemas';

export interface IRepresentativeContext extends IRepresentantResult {
  loading: boolean;
  loadingDelete: boolean;
  loadingView: boolean;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  handleRefresh: () => void;
  saveRepresentative: (values: IRepresentant) => Promise<void>;
  getRepresentative: (id: string) => Promise<void>;
  setSnackbar: Function;
  deleteRepresentative: Function;
  representative: IRepresentant | null;
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export const RepresentativeContext = createContext<IRepresentativeContext>(
  {} as IRepresentativeContext
);

type Props = {
  children: ReactNode;
};

export const RepresentativeProvider = ({ children }: Props) => {
  const [representatives, setRepresentatives] = useState<IRepresentantResult>({
    docs: [],
  } as unknown as IRepresentantResult);
  const [representative, setRepresentative] = useState<IRepresentant | null>(
    null
  );

  const { loading, showLoader, hideLoader } = useLoader(false);
  const loadingDelete = useLoader(false);
  const loadingView = useLoader(false);

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const form = useForm<IRepresentant>({
    defaultValues: RepresentantDefaultValues,
    resolver: representativeValidation,
    mode: 'onBlur',
  });

  const getRepresentatives = async ({ page, limit }: IPaginatedRequest) => {
    setRepresentative(null);

    try {
      showLoader();

      await RepresentativeService.findAllRepresentative({ page, limit }).then(
        setRepresentatives
      );
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  const deleteRepresentative = async (id: string) => {
    if (!id) {
      return;
    }

    try {
      loadingDelete.showLoader();
      await RepresentativeService.deleteRepresentantById(id);

      handleRefresh();

      setSnackbar({
        message: 'Representante apagado com sucesso',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível apagar o representante',
        variant: 'error',
      });
    } finally {
      loadingDelete.hideLoader();
    }
  };

  const getRepresentative = async (id: string) => {
    setRepresentative(null);

    if (!id) {
      return;
    }

    try {
      showLoader();

      const data = await RepresentativeService.findById(id);
      setRepresentative(data);
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  const saveRepresentative = async (values: IRepresentant) => {
    try {
      showLoader();

      await RepresentativeService.save(values);

      if (values?._id) {
        setSnackbar({
          message: 'Representante atualizado com sucesso',
          variant: 'success',
        });
      } else {
        setSnackbar({
          message: 'Representante criado com sucesso',
          variant: 'success',
        });
        form.reset(RepresentantDefaultValues);
        setRepresentative(null);
      }
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  const handlePreviousPage = () => {
    getRepresentatives({
      page: representatives.prevPage,
    });
  };

  const handleNextPage = () => {
    getRepresentatives({
      page: representatives.nextPage,
    });
  };

  const handleRefresh = () => {
    getRepresentatives({});
  };

  useEffect(() => {
    form.reset(representative ?? RepresentantDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [representative]);

  return (
    <RepresentativeContext.Provider
      value={{
        loading,
        handlePreviousPage,
        handleNextPage,
        handleRefresh,
        setSnackbar,
        deleteRepresentative,
        saveRepresentative,
        getRepresentative,
        representative,
        loadingDelete: loadingDelete.loading,
        loadingView: loadingView.loading,
        ...representatives,
      }}
    >
      <FormProvider {...form}>{children}</FormProvider>

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
    </RepresentativeContext.Provider>
  );
};
